import React, { useMemo, useRef, useState } from 'react';
import { Avatar, Button, Flex, message, Select, Spin, Typography } from 'antd';
import Tag from '@/components/Tag';
import debounce from 'lodash/debounce';
import { SearchOutlined, UserOutlined } from '@ant-design/icons';
import { SearchUserInfo, UserStageType } from '@/types/member';
import { queryUserLeads } from '@/services/member';

const { Text } = Typography;

type UserInfo = SearchUserInfo & {
  type: UserStageType;
};

export type IProps = {
  searchTypes: UserStageType[]; // 搜索类型，1 表示用户，2 表示线索
  placeholder?: string; // 搜索框提示
  isTransfer?: boolean; // 是否是除换绑手机号之外的场景
  onChange?: (value: UserInfo) => void;
};

export default function SearchUserSelect({
  searchTypes = [],
  placeholder = '请输入昵称/手机号搜索用户',
  onChange,
  isTransfer = true,
}: IProps) {
  const [searchValue, setSearchValue] = useState(''); // 搜索框内容

  const [fetching, setFetching] = useState(false);
  const [options, setOptions] = useState<UserInfo[]>([]);
  const fetchRef = useRef(0);

  // 检查手机号格式
  function checkPhone(value: string | undefined) {
    if (!value?.trim()) {
      message.error('请输入昵称/手机号');
      return false;
    }
    return true;
  }

  const debounceFetcher = useMemo(() => {
    const loadOptions = async (value: string) => {
      try {
        if (!value) {
          return;
        }
        fetchRef.current += 1;
        const fetchId = fetchRef.current;
        setOptions([]);
        setFetching(true);
        // 请求搜索用户接口
        const res = await queryUserLeads({
          search_types: searchTypes,
          keywords: value,
        });
        if (fetchId !== fetchRef.current) {
          // for fetch callback order
          return;
        }
        const { user, leads } = res;
        const list = [] as any[];
        // 用户
        if (user?.length > 0) {
          user.forEach((item) => {
            list.push({
              type: UserStageType.USER,
              ...item,
            });
          });
        } else if (!isTransfer && value) {
          list[0] = {
            type: UserStageType.USER,
            name: value,
            phone_number: value,
            user_id: 0,
            avatar: '',
            wx_number: '',
            is_member: 0,
          };
        }
        // 线索
        if (leads?.length > 0) {
          leads.forEach((item) => {
            list.push({
              type: UserStageType.LEADS,
              ...item,
            });
          });
        }

        setOptions(list);
      } catch (error) {
      } finally {
        setFetching(false);
      }
    };

    return debounce(loadOptions, 800);
  }, []);

  return (
    <Select
      showSearch
      filterOption={false}
      placeholder={placeholder}
      optionLabelProp="label"
      searchValue={searchValue}
      onSearch={(value) => {
        setSearchValue(value);
        if (value) {
          debounceFetcher(value);
        }
      }}
      onSelect={(value) => {
        const user = options.find((item) => item.user_id === value);
        setSearchValue(user?.phone_number || '');
        if (user) {
          onChange?.(user);
        }
      }}
      onInputKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault(); // 阻止默认行为，防止清空输入框
          e.stopPropagation();
          if (!checkPhone(searchValue)) {
            return;
          }
          debounceFetcher(searchValue as string);
        }
      }}
      notFoundContent={fetching ? <Spin size="small" /> : null}
      style={{ width: '100%' }}
      suffixIcon={
        <Button
          type="text"
          size="small"
          icon={<SearchOutlined />}
          onClick={() => {
            if (!checkPhone(searchValue)) {
              return;
            }
            debounceFetcher(searchValue as string);
          }}
        />
      }
    >
      {options.map((user) => (
        <Select.Option key={user.user_id} value={user.user_id} label={user.name}>
          <Flex align="center" justify="space-between">
            <Flex align="center" gap={12}>
              {user.avatar ? (
                <Avatar src={user.avatar} size={32} />
              ) : (
                <Avatar icon={<UserOutlined />} size={32} />
              )}
              <Flex vertical>
                <Text ellipsis style={{ maxWidth: 120 }}>
                  {user.name + (user.remarks ? ` (${user.remarks})` : '')}
                </Text>
                <span style={{ color: '#999', fontSize: 12 }}>{user.phone_number}</span>
              </Flex>
            </Flex>
            {searchTypes.length > 1 && (
              <div>
                {user.type === UserStageType.LEADS && <Tag color="green">线索</Tag>}
                {user.type === UserStageType.USER && <Tag color="blue">用户</Tag>}
              </div>
            )}
          </Flex>
        </Select.Option>
      ))}
    </Select>
  );
}
